import './settings/settings-leaf.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { templateApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { useLocalizedContent } from '../i18n/localized.ts';
import { Alert, Button, Checkbox, EmptyState, LoadingBlock, TextArea, TextInput } from './ui.tsx';
import { MoneyInput } from './money-input.tsx';
import LocalizedFields, {
  emptyTranslationDraft,
  translationDraftFrom,
  translationPayload,
  type TranslationDraft,
} from './localized-fields.tsx';
import type { SupportedLanguage } from '@shared/constants/languages.ts';
import { emptyTemplateTags, type TemplateTags } from '@shared/constants/template-taxonomy.ts';
import { TaxonomyFields } from './taxonomy-fields.tsx';
import type { ServiceTemplate } from '@shared/types/index.ts';

/**
 * The company's task library (spec §10.9).
 *
 * A task is the unit a room type is built from and the line an estimator ticks
 * on a quote, so this is where its name, its default time and whether the
 * customer ever sees it are decided. Reading needs `templates:read`; changing
 * anything needs `templates:write`, which an Estimator does not have.
 *
 * Editing a task never rewrites a quote that already exists, and it does not
 * rewrite the room types that link it either: a room type keeps its own
 * minutes override, and a quote copies both in when the room is added
 * (spec §8.11).
 */

/** Sentinel for "the add form is open", which has no id yet. */
const NEW_TASK = '__new__';

/** The field paths this form has a control for. See `reportError`. */
const RENDERED_FIELDS = new Set([
  'name',
  'category',
  'customerDescription',
  'defaultMinutes',
  'minimumMinutes',
  'maximumMinutes',
]);

/**
 * The largest display order `createServiceTemplateSchema` will accept. Going
 * over it is a 400, not a big number.
 */
const MAX_DISPLAY_ORDER = 10_000;

/**
 * Where a newly created task goes: after the last one.
 *
 * Counting the list is not the same thing. Orders go non-contiguous as soon as
 * a task is hidden — 0, 2, 3 has a count of 3, which ties the existing last
 * task and lets the API's secondary sort by name decide the order.
 *
 * Capped, because "after the last one" stops being available at the top of the
 * range: a library holding a task at 10,000 would otherwise ask for 10,001 and
 * have every new task rejected by the API. Tying with the last task at the
 * ceiling is a worse sort than following it, but it is a saved task rather than
 * a form that cannot be submitted.
 */
function nextDisplayOrder(items: ServiceTemplate[] | undefined): number {
  if (!items || items.length === 0) return 0;
  return Math.min(MAX_DISPLAY_ORDER, Math.max(...items.map((item) => item.displayOrder)) + 1);
}

interface Draft {
  name: string;
  category: string;
  customerDescription: string;
  /**
   * The name and description in the company's OTHER languages (see localized-text.ts). The two
   * fields above stay the company's own wording, which is what a customer's
   * quote prints; these are what staff reading the app in another language
   * see. Blanks are kept rather than dropped so a box cleared in the form
   * stays cleared while it is open.
   */
  nameTranslations: TranslationDraft;
  customerDescriptionTranslations: TranslationDraft;
  categoryTranslations: TranslationDraft;
  /** Raw input, so a half-typed number is not rounded under the cursor. */
  defaultMinutes: string;
  /** Empty means "no bound", which is a different answer from zero. */
  minimumMinutes: string;
  maximumMinutes: string;
  isTaxable: boolean;
  showOnCustomerQuote: boolean;
  isActive: boolean;
  categoryCode: string;
  tags: TemplateTags;
  isFavorite: boolean;
  /** Cost basis (0118). Null rate = the company default; amounts in cents. */
  costLoadedHourlyCents: number | null;
  costMaterialsCents: number | null;
  costTravelCents: number | null;
}

function emptyDraft(): Draft {
  return {
    name: '',
    category: '',
    customerDescription: '',
    nameTranslations: emptyTranslationDraft(),
    customerDescriptionTranslations: emptyTranslationDraft(),
    categoryTranslations: emptyTranslationDraft(),
    defaultMinutes: '15',
    minimumMinutes: '',
    maximumMinutes: '',
    isTaxable: true,
    showOnCustomerQuote: true,
    isActive: true,
    categoryCode: '',
    tags: emptyTemplateTags(),
    isFavorite: false,
    costLoadedHourlyCents: null,
    costMaterialsCents: null,
    costTravelCents: null,
  };
}

function draftFrom(template: ServiceTemplate): Draft {
  return {
    name: template.name,
    category: template.category ?? '',
    customerDescription: template.customerDescription ?? '',
    nameTranslations: translationDraftFrom(template.nameTranslations),
    customerDescriptionTranslations: translationDraftFrom(template.customerDescriptionTranslations),
    categoryTranslations: translationDraftFrom(template.categoryTranslations),
    defaultMinutes: String(template.defaultMinutes),
    minimumMinutes: template.minimumMinutes === null ? '' : String(template.minimumMinutes),
    maximumMinutes: template.maximumMinutes === null ? '' : String(template.maximumMinutes),
    isTaxable: template.isTaxable,
    showOnCustomerQuote: template.showOnCustomerQuote,
    isActive: template.isActive,
    categoryCode: template.categoryCode ?? '',
    tags: template.tags ?? emptyTemplateTags(),
    isFavorite: template.isFavorite ?? false,
    costLoadedHourlyCents: template.costLoadedHourlyCents ?? null,
    costMaterialsCents: template.costMaterialsCents ? template.costMaterialsCents : null,
    costTravelCents: template.costTravelCents ? template.costTravelCents : null,
  };
}

/**
 * Blank means "no bound" — the task is unlimited at that end — which is a
 * different answer from zero, so it cannot be coerced to 0 here.
 */
function parseMinutes(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const minutes = Number(trimmed);
  if (!Number.isFinite(minutes)) return null;
  return Math.max(0, Math.round(minutes));
}

function draftPayload(draft: Draft, baseLanguage: SupportedLanguage, includeCost = true): Record<string, unknown> {
  const name = draft.name.trim();
  const customerDescription = draft.customerDescription.trim();
  const category = draft.category.trim();
  return {
    name,
    category,
    customerDescription,
    // The base language's entry comes from the fields above, not from the
    // form's own boxes — see `translationPayload` for why they must agree.
    nameTranslations: translationPayload(draft.nameTranslations, baseLanguage, name),
    customerDescriptionTranslations: translationPayload(
      draft.customerDescriptionTranslations,
      baseLanguage,
      customerDescription,
    ),
    categoryTranslations: translationPayload(draft.categoryTranslations, baseLanguage, category),
    // Guarded by the submit button, which stays disabled until this parses.
    defaultMinutes: parseMinutes(draft.defaultMinutes) ?? 0,
    minimumMinutes: parseMinutes(draft.minimumMinutes),
    maximumMinutes: parseMinutes(draft.maximumMinutes),
    isTaxable: draft.isTaxable,
    showOnCustomerQuote: draft.showOnCustomerQuote,
    isActive: draft.isActive,
    categoryCode: draft.categoryCode || null,
    tags:
      draft.tags.segments.length + draft.tags.jobTypes.length + draft.tags.floorTypes.length === 0 ? null : draft.tags,
    isFavorite: draft.isFavorite,
    // Only someone who may see the cost basis sends it; the server ignores it otherwise.
    ...(includeCost
      ? {
          costLoadedHourlyCents: draft.costLoadedHourlyCents,
          costMaterialsCents: draft.costMaterialsCents ?? 0,
          costTravelCents: draft.costTravelCents ?? 0,
        }
      : {}),
  };
}

export default function ServiceTemplatesSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can, company } = useSession();
  const localized = useLocalizedContent();
  /*
    Which language a row's own `name` and `customerDescription` are written in
    comes from the ROW, not from the company.

    `company.language` is what the company sends in and the owner changes it in
    Settings; the row was written once and stays written in whatever it was
    written in. Reading the mutable setting would have this form believe the
    English name it is showing is the Spanish one, and file it under `es` on
    the next save — taking the real Spanish with it.

    A brand-new task has no row yet, so it records the language it is being
    typed in, which is the one the person is reading.
  */
  const baseLanguageOf = (template: ServiceTemplate | null) => template?.textLanguage ?? localized.language;

  const [showHidden, setShowHidden] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /*
    Always the whole library, hidden tasks included; the toggle only decides
    what is drawn. That makes the toggle instant, and it is the same query the
    room-type editor reads — a task added here shows up in its picker without a
    second request. Placing a new task after the last one needs the hidden rows
    too, since they hold positions nothing on screen can see.
  */
  const services = useQuery({
    queryKey: ['service-templates', true],
    queryFn: () => templateApi.services(true),
  });

  const writable = can('templates:write');
  const all = services.data?.items ?? [];
  const items = showHidden ? all : all.filter((template) => template.isActive);

  function refresh() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['service-templates'] }),
      // A hidden task changes what a room type's editor can name and offer, and
      // the room rows count their tasks — so the room library is stale too.
      queryClient.invalidateQueries({ queryKey: ['room-templates'] }),
    ]);
  }

  /*
    Field errors are only silent-safe when the form can actually show them.
    Anything the server names that this form has no control for — and the
    merged minimum/maximum check can name one — falls back to the alert, rather
    than leaving Save doing nothing visible.
  */
  function reportError(caught: unknown, fallback: string) {
    if (!(caught instanceof ApiRequestError)) {
      setFieldErrors({});
      setError(fallback);
      return;
    }

    setFieldErrors(caught.fields);
    const paths = Object.keys(caught.fields);
    const shown = paths.filter((path) => RENDERED_FIELDS.has(path));
    setError(paths.length > 0 && shown.length === paths.length ? null : caught.message);
  }

  /** The row being edited, and so the language its base texts are written in. */
  const editingRow = all.find((template) => template.id === editing) ?? null;
  const baseLanguage = baseLanguageOf(editing === NEW_TASK ? null : editingRow);

  const save = useMutation({
    mutationFn: () =>
      editing === NEW_TASK
        ? templateApi.createService({
            ...draftPayload(draft, baseLanguage, can('reports:read')),
            textLanguage: baseLanguage,
            displayOrder: nextDisplayOrder(all),
          })
        : templateApi.updateService(editing ?? '', draftPayload(draft, baseLanguage, can('reports:read'))),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setDraft(emptyDraft());
      setError(null);
      setFieldErrors({});
    },
    onError: (caught) => reportError(caught, t('serviceTemplates.saveError')),
  });

  /*
    Clearing on the way out matters as much as setting on the way in: a hide
    that failed and then succeeded on a retry would otherwise leave the page
    still saying it could not be saved, over a task that is now hidden.
  */
  function clearError() {
    setError(null);
    setFieldErrors({});
  }

  const hide = useMutation({
    mutationFn: (id: string) => templateApi.deactivateService(id),
    onSuccess: async () => {
      await refresh();
      clearError();
    },
    onError: (caught) => reportError(caught, t('serviceTemplates.saveError')),
  });

  const restore = useMutation({
    mutationFn: (id: string) => templateApi.updateService(id, { isActive: true }),
    onSuccess: async () => {
      await refresh();
      clearError();
    },
    onError: (caught) => reportError(caught, t('serviceTemplates.saveError')),
  });

  function startAdd() {
    setEditing(NEW_TASK);
    setDraft(emptyDraft());
    setError(null);
    setFieldErrors({});
  }

  function startEdit(template: ServiceTemplate) {
    setEditing(template.id);
    setDraft(draftFrom(template));
    setError(null);
    setFieldErrors({});
  }

  function cancel() {
    setEditing(null);
    setDraft(emptyDraft());
    setError(null);
    setFieldErrors({});
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  const busy = save.isPending || hide.isPending || restore.isPending;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="task-library-heading">
      <div className="work-section-heading">
        <h2 id="task-library-heading" className="text-lg font-semibold">
          {t('serviceTemplates.heading')}
        </h2>
        {/*
          "New task", not "Add task": the room-type editor further down the
          same page already has an "Add task" button, and that one puts an
          existing task onto a room rather than writing a new one into the
          library. Two buttons with one name on one page is a coin toss for
          anyone navigating by label.

          Held shut until the library has arrived, and while a save is in
          flight. Both are about where a new task lands: `nextDisplayOrder`
          reads the list, so an empty one — still loading, or failed — puts the
          new task at 0, tied with whatever is already there. And Cancel stays
          live during a slow save on purpose, so without `busy` an owner can
          cancel, start a second draft, and have the first save's `onSuccess`
          clear it out from under them.
        */}
        {writable && editing === null ? (
          <Button onClick={startAdd} disabled={!services.isSuccess || busy}>
            {t('serviceTemplates.addTask')}
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('serviceTemplates.description')}</p>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('libraryTaxonomy.estimates')}</p>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {services.isError ? (
        <div className="mb-4">
          <Alert tone="error">{t('serviceTemplates.loadError')}</Alert>
        </div>
      ) : null}

      <Checkbox
        label={t('serviceTemplates.showHidden')}
        checked={showHidden}
        onChange={(event) => {
          const next = event.target.checked;
          setShowHidden(next);
          /*
            Only throw the draft away when the row holding it is about to leave
            the list — turning the option off while editing a hidden task.
            Everything else stays on screen either way, and dropping an edit
            someone was halfway through for a filter change is not a trade the
            filter is worth.
          */
          const editedLeaves = !next && all.some((item) => item.id === editing && !item.isActive);
          if (editedLeaves) cancel();
        }}
      />

      {editing === NEW_TASK ? (
        <ServiceTemplateForm
          draft={draft}
          setDraft={setDraft}
          baseLanguage={baseLanguage}
          fieldErrors={fieldErrors}
          busy={save.isPending}
          submitLabel={t('serviceTemplates.createTask')}
          onSubmit={handleSubmit}
          onCancel={cancel}
        />
      ) : null}

      {services.isLoading ? <LoadingBlock label={t('serviceTemplates.loading')} /> : null}

      {/*
        `isSuccess`, not `!isLoading`: a failed request leaves the data
        undefined and the list empty, which drew "No tasks yet — add one"
        directly under the load-error alert, over a disabled button. An outage
        is not an empty library.
      */}
      {services.isSuccess && items.length === 0 && editing !== NEW_TASK ? (
        <EmptyState title={t('serviceTemplates.emptyTitle')}>
          <p>{t('serviceTemplates.emptyMessage')}</p>
        </EmptyState>
      ) : null}

      <ul className="divide-y divide-[color:var(--color-line)]">
        {items.map((template) => {
          const description = localized.optionalText(
            template.customerDescription,
            template.customerDescriptionTranslations,
          );

          if (editing === template.id) {
            return (
              <li key={template.id} className="py-3">
                <ServiceTemplateForm
                  draft={draft}
                  setDraft={setDraft}
                  baseLanguage={baseLanguage}
                  fieldErrors={fieldErrors}
                  busy={save.isPending}
                  submitLabel={t('serviceTemplates.saveTask')}
                  onSubmit={handleSubmit}
                  onCancel={cancel}
                />
              </li>
            );
          }

          return (
            <li key={template.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="w-full min-w-0 sm:min-w-56 sm:flex-1">
                <p className="font-medium">
                  {/* Read in the viewer's language, like every other list of
                      room and task names in the app (see localized-text.ts). The form below
                      labels which language each field is. */}
                  {localized.text(template.name, template.nameTranslations)}
                  {!template.isActive ? (
                    <span className="ml-2 badge border-[color:var(--color-muted)] text-[color:var(--color-muted)]">
                      {t('serviceTemplates.hidden')}
                    </span>
                  ) : null}
                </p>
                {/*
                  Resolved before the decision to draw it: a task translated
                  into the reader's language but left blank in the company's
                  own has a description to show and no base text to gate on.
                */}
                {description ? <p className="text-sm text-[color:var(--color-muted)]">{description}</p> : null}
                <p className="text-sm text-[color:var(--color-muted)]">
                  {t('serviceTemplates.minutesSummary', { minutes: template.defaultMinutes })}
                  {' · '}
                  {localized.optionalText(template.category, template.categoryTranslations) ??
                    t('serviceTemplates.uncategorized')}
                  {/*
                    The bounds and the two flags are only worth a row's width
                    when they are not the default: every task is taxable and on
                    the customer's copy until someone says otherwise.
                  */}
                  {template.minimumMinutes !== null
                    ? ` · ${t('serviceTemplates.minimumSummary', { minutes: template.minimumMinutes })}`
                    : ''}
                  {template.maximumMinutes !== null
                    ? ` · ${t('serviceTemplates.maximumSummary', { minutes: template.maximumMinutes })}`
                    : ''}
                  {!template.isTaxable ? ` · ${t('serviceTemplates.notTaxable')}` : ''}
                  {!template.showOnCustomerQuote ? ` · ${t('serviceTemplates.internalOnly')}` : ''}
                </p>
              </div>

              {writable ? (
                <div className="flex flex-wrap gap-2">
                  <Button disabled={editing !== null || busy} onClick={() => startEdit(template)}>
                    {t('serviceTemplates.edit')}
                  </Button>
                  {template.isActive ? (
                    <Button
                      variant="danger"
                      disabled={editing !== null || busy}
                      onClick={() => hide.mutate(template.id)}
                    >
                      {t('serviceTemplates.hide')}
                    </Button>
                  ) : (
                    <Button disabled={editing !== null || busy} onClick={() => restore.mutate(template.id)}>
                      {t('serviceTemplates.restore')}
                    </Button>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {writable ? <p className="field-hint mt-4">{t('serviceTemplates.hideNote')}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The add/edit form
// ---------------------------------------------------------------------------

interface ServiceTemplateFormProps {
  draft: Draft;
  setDraft: (update: (current: Draft) => Draft) => void;
  /** The language the base name and description are written in. */
  baseLanguage: SupportedLanguage;
  fieldErrors: Record<string, string>;
  busy: boolean;
  submitLabel: string;
  onSubmit: (event: FormEvent) => void;
  onCancel: () => void;
}

function ServiceTemplateForm({
  draft,
  setDraft,
  baseLanguage,
  fieldErrors,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: ServiceTemplateFormProps) {
  const { t } = useTranslation();
  const { can, company } = useSession();

  return (
    <form onSubmit={onSubmit} noValidate className="mb-6 rounded-md border border-[color:var(--color-line)] p-4">
      {/*
        Everything the save is carrying goes read-only while it is in flight.
        The mutation closed over the draft as it was at submit time, so edits
        made during a slow save stay on screen, look accepted, and then vanish
        when the response closes the form. Cancel stays outside the fieldset:
        a save that hangs must not trap the user in it.
      */}
      <fieldset disabled={busy} className="contents">
        <TextInput
          label={t('serviceTemplates.nameLabel')}
          required
          value={draft.name}
          error={fieldErrors.name}
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
        />
        <TextInput
          label={t('serviceTemplates.categoryLabel')}
          hint={t('serviceTemplates.categoryHint')}
          value={draft.category}
          error={fieldErrors.category}
          onChange={(event) => setDraft((current) => ({ ...current, category: event.target.value }))}
        />
        <TaxonomyFields
          kind="task"
          categoryCode={draft.categoryCode}
          tags={draft.tags}
          isFavorite={draft.isFavorite}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        />
        <p className="field-hint -mt-2 mb-4">{t('libraryTaxonomy.estimates')}</p>
        <TextArea
          label={t('serviceTemplates.customerDescriptionLabel')}
          rows={2}
          hint={t('serviceTemplates.customerDescriptionHint')}
          value={draft.customerDescription}
          error={fieldErrors.customerDescription}
          onChange={(event) => setDraft((current) => ({ ...current, customerDescription: event.target.value }))}
        />

        <LocalizedFields
          baseLanguage={baseLanguage}
          fields={[
            {
              key: 'name',
              label: t('serviceTemplates.nameLabel'),
              valueFor: (language) => draft.nameTranslations[language],
              onChange: (language, value) =>
                setDraft((current) => ({
                  ...current,
                  nameTranslations: { ...current.nameTranslations, [language]: value },
                })),
            },
            {
              key: 'category',
              label: t('serviceTemplates.categoryLabel'),
              valueFor: (language) => draft.categoryTranslations[language],
              onChange: (language, value) =>
                setDraft((current) => ({
                  ...current,
                  categoryTranslations: { ...current.categoryTranslations, [language]: value },
                })),
            },
            {
              key: 'customerDescription',
              label: t('serviceTemplates.customerDescriptionLabel'),
              multiline: true,
              valueFor: (language) => draft.customerDescriptionTranslations[language],
              onChange: (language, value) =>
                setDraft((current) => ({
                  ...current,
                  customerDescriptionTranslations: { ...current.customerDescriptionTranslations, [language]: value },
                })),
            },
          ]}
        />

        <div className="grid gap-x-4 sm:grid-cols-3">
          <TextInput
            label={t('serviceTemplates.defaultMinutesLabel')}
            required
            type="number"
            min={0}
            max={1000}
            className="text-right"
            hint={t('serviceTemplates.defaultMinutesHint')}
            value={draft.defaultMinutes}
            error={fieldErrors.defaultMinutes}
            onChange={(event) => setDraft((current) => ({ ...current, defaultMinutes: event.target.value }))}
          />
          {/*
            Blank is not zero on these two: an empty box is "no bound at this
            end", and 0 would pin a task to nothing at all.
          */}
          <TextInput
            label={t('serviceTemplates.minimumMinutesLabel')}
            type="number"
            min={0}
            max={1000}
            className="text-right"
            hint={t('serviceTemplates.boundsHint')}
            value={draft.minimumMinutes}
            error={fieldErrors.minimumMinutes}
            onChange={(event) => setDraft((current) => ({ ...current, minimumMinutes: event.target.value }))}
          />
          <TextInput
            label={t('serviceTemplates.maximumMinutesLabel')}
            type="number"
            min={0}
            max={1000}
            className="text-right"
            value={draft.maximumMinutes}
            error={fieldErrors.maximumMinutes}
            onChange={(event) => setDraft((current) => ({ ...current, maximumMinutes: event.target.value }))}
          />
        </div>

        {/*
          Cost basis (0118): what one room of this task costs the company, for
          the margin each quote line is expected to make. Staff only; never on
          a customer's quote.
        */}
        {can('reports:read') && (
          <fieldset className="mb-2" data-testid="service-template-cost-basis">
            <legend className="mb-1 text-sm font-semibold">{t('serviceTemplates.costBasisHeading')}</legend>
            <p className="field-hint mb-2">{t('serviceTemplates.costBasisHint')}</p>
            <div className="grid gap-x-4 sm:grid-cols-3">
              <MoneyInput
                label={t('serviceTemplates.costLoadedRateLabel')}
                hint={t('serviceTemplates.costLoadedRateHint')}
                valueCents={draft.costLoadedHourlyCents}
                currencyCode={company?.currencyCode}
                min={0}
                onChangeCents={(cents) => setDraft((current) => ({ ...current, costLoadedHourlyCents: cents }))}
              />
              <MoneyInput
                label={t('serviceTemplates.costMaterialsLabel')}
                valueCents={draft.costMaterialsCents}
                currencyCode={company?.currencyCode}
                min={0}
                onChangeCents={(cents) => setDraft((current) => ({ ...current, costMaterialsCents: cents }))}
              />
              <MoneyInput
                label={t('serviceTemplates.costTravelLabel')}
                valueCents={draft.costTravelCents}
                currencyCode={company?.currencyCode}
                min={0}
                onChangeCents={(cents) => setDraft((current) => ({ ...current, costTravelCents: cents }))}
              />
            </div>
          </fieldset>
        )}

        <Checkbox
          label={t('serviceTemplates.taxableLabel')}
          hint={t('serviceTemplates.taxableHint')}
          checked={draft.isTaxable}
          onChange={(event) => setDraft((current) => ({ ...current, isTaxable: event.target.checked }))}
        />
        <Checkbox
          label={t('serviceTemplates.showOnQuoteLabel')}
          hint={t('serviceTemplates.showOnQuoteHint')}
          checked={draft.showOnCustomerQuote}
          onChange={(event) => setDraft((current) => ({ ...current, showOnCustomerQuote: event.target.checked }))}
        />
        <Checkbox
          label={t('serviceTemplates.availableLabel')}
          hint={t('serviceTemplates.availableHint')}
          checked={draft.isActive}
          onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
        />
      </fieldset>

      <div className="flex gap-3">
        <Button
          type="submit"
          variant="primary"
          busy={busy}
          disabled={!draft.name.trim() || parseMinutes(draft.defaultMinutes) === null}
        >
          {submitLabel}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}
